import { MedplumClient } from '@medplum/core';
import { Patient } from '@medplum/fhirtypes';

/**
 * Sets up a premium trial subscription for a newly registered patient
 * @param medplum The MedplumClient instance
 * @param patient The patient resource to update, or patient ID
 * @param trialDays Number of days for the trial period (default: 7)
 * @returns The updated patient resource with subscription extensions
 */
export async function setupPremiumTrial(
  medplum: MedplumClient, 
  patient: Patient | string,
  trialDays = 7
): Promise<Patient> {
  try {
    // If patient is a string (ID), fetch the full resource
    const patientResource = typeof patient === 'string' 
      ? await medplum.readResource('Patient', patient)
      : patient;
    
    // Set trial period (default 7 days)
    const trialEndDate = new Date();
    trialEndDate.setDate(trialEndDate.getDate() + trialDays);
    
    const extensions = patientResource.extension || [];
    
    // Filter out any existing subscription extensions
    const filteredExtensions = extensions.filter(e => 
      !e.url.startsWith('https://progressnotes.app/fhir/StructureDefinition/subscription-') &&
      e.url !== 'https://progressnotes.app/fhir/StructureDefinition/trial-end-date'
    );
    
    // Add new trial extensions
    const trialExtensions = [
      {
        url: 'https://progressnotes.app/fhir/StructureDefinition/subscription-status',
        valueString: 'trial' // Set to 'trial' to enable trial features
      },
      {
        url: 'https://progressnotes.app/fhir/StructureDefinition/subscription-tier',
        valueString: 'premium' // Set to 'premium' for trial period
      },
      {
        url: 'https://progressnotes.app/fhir/StructureDefinition/trial-end-date',
        valueDateTime: trialEndDate.toISOString()
      }
    ];
    
    // Update the patient with new extensions
    const updatedPatient = await medplum.updateResource({
      ...patientResource,
      extension: [...filteredExtensions, ...trialExtensions]
    });
    
    console.log('Trial extensions added to patient:', {
      subscriptionStatus: updatedPatient.extension?.find(e => 
        e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-status')?.valueString,
      subscriptionTier: updatedPatient.extension?.find(e => 
        e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-tier')?.valueString,
      trialEndDate: updatedPatient.extension?.find(e => 
        e.url === 'https://progressnotes.app/fhir/StructureDefinition/trial-end-date')?.valueDateTime
    });
    
    return updatedPatient;
  } catch (error) {
    console.error('Error setting up premium trial:', error);
    throw error;
  }
}